/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRequest } from 'ahooks';
import { usagePermissionApi } from '@coze-arch/bot-api';

export interface UsageQuery {
  source?: number;
  credential_id?: number;
  agent_id?: number;
  model_name?: string;
  end_user_id?: string;
  biz_type?: number;
  start?: number;
  end?: number;
}

const queryKey = (query?: UsageQuery) => JSON.stringify(query ?? {});

export const useGetUsageAggregate = ({
  spaceId,
  query,
  groupBy,
}: {
  spaceId?: string;
  query?: UsageQuery;
  groupBy?: string;
}) => {
  const { loading, data } = useRequest(
    () =>
      usagePermissionApi.AggregateUsage({
        space_id: spaceId,
        ...query,
        group_by: groupBy,
      }),
    { ready: !!spaceId, refreshDeps: [spaceId, groupBy, queryKey(query)] },
  );
  return {
    loading,
    items: data?.data?.items ?? [],
    totalTokens: data?.data?.total_tokens ?? 0,
  };
};

export const useGetUsageList = ({
  spaceId,
  query,
  page,
  size,
}: {
  spaceId?: string;
  query?: UsageQuery;
  page: number;
  size: number;
}) => {
  const { loading, data } = useRequest(
    () =>
      usagePermissionApi.ListUsage({
        space_id: spaceId,
        ...query,
        page,
        size,
      }),
    {
      ready: !!spaceId,
      refreshDeps: [spaceId, page, size, queryKey(query)],
    },
  );
  return {
    loading,
    items: data?.data?.items ?? [],
    hasMore: data?.data?.has_more,
  };
};

export const useGetUsageFacet = ({ spaceId }: { spaceId?: string }) => {
  const { loading, data } = useRequest(
    () => usagePermissionApi.UsageFacet({ space_id: spaceId }),
    { ready: !!spaceId, refreshDeps: [spaceId] },
  );
  return {
    loading,
    models: data?.data?.models ?? [],
    oauthApps: data?.data?.oauth_apps ?? [],
    agents: data?.data?.agents ?? [],
    endUsers: data?.data?.end_users ?? [],
  };
};
